import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { isApiError } from '../api/errors'
import { SUPPORT_EMAIL } from '../config'

type Props = { children: ReactNode }
type State = { error: Error | null }

/**
 * Last line of defence. Catches render failures and the API failures that are the
 * archive's own fault, so a fault degrades into a readable page rather than a blank
 * one — which docs/api.md §1 treats as a compliance property, not a nicety.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Logged locally only. This site has no analytics and no error reporting
    // service, and nothing about a reader leaves the browser.
    console.error('Unhandled error in the disclosure archive', error, info)
  }

  render() {
    const { error } = this.state
    return error ? <ErrorFallback error={error} /> : this.props.children
  }
}

/**
 * The page a reader actually sees. Split from the boundary so it can be rendered
 * and checked on its own — a boundary only shows its output when something has
 * already gone wrong, which is hard to reach on purpose.
 */
export function ErrorFallback({ error }: { error: Error }) {
  // The API's own message where there is one; no guess at a cause where there is
  // not.
  const detail = isApiError(error) ? error.message : null

  return (
      <div className="stack-lg">
      <header className="page-header">
        <h1>This page could not be displayed</h1>
      </header>

      <div className="status status-error" role="alert">
        {detail ? <p className="status-title">{detail}</p> : null}
        <div className="status-body">
          <p>
            Something went wrong on our side, so the archive could not be shown. Your search was
            not the cause.
          </p>
          <p>
            If it keeps happening, please contact{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and include the address of
            the page you were trying to open.
          </p>
        </div>
        <div className="status-action">
          <button type="button" className="button" onClick={() => window.location.reload()}>
            Reload the page
          </button>{' '}
          <Link to="/search" className="button button-quiet">
            Back to search
          </Link>
        </div>
      </div>
    </div>
  )
}
